import { z } from 'zod';

import { ControlType } from '@/constants/institution';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsDecisionMaker,
  type InstitutionDetailsV2ContactablePerson,
  type InstitutionDetailsV2Partial,
  institutionDetailsIndividualBaseSchema,
  institutionDetailsNationalClientIdentifierSchema,
} from '@/services/api/profile';
import { getNationalClientIdentifierFormatError, reportNationalClientIdentifierFormatError } from '@/services/api/profile/national-client-identifier-format';
import type { DeepPartial } from '@/types/generics';
import { toDatePartsFromString } from '@/utils/date';

export const institutionIndividualSchema = institutionDetailsIndividualBaseSchema
  .omit({
    dateOfBirth: true,
  })
  .extend({
    dateOfBirth: z.object({
      day: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('institutionDetails.individuals.individual.errors.dateOfBirthDay'),
      }),
      month: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('institutionDetails.individuals.individual.errors.dateOfBirthMonth'),
      }),
      year: z.string().regex(/^\d{4}$/, {
        error: i18n.t('institutionDetails.individuals.individual.errors.dateOfBirthYear'),
      }),
    }),
  })
  .and(institutionDetailsNationalClientIdentifierSchema)
  .superRefine(async ({ nationality, nationalClientIdentifier, nationalClientIdentifierType }, ctx) => {
    const error = await getNationalClientIdentifierFormatError({
      country: nationality,
      identifier: nationalClientIdentifier,
      identifierType: nationalClientIdentifierType,
    });

    if (error) ctx.addIssue({ code: 'custom', message: error, path: ['nationalClientIdentifier'] });
  });

export type InstitutionIndividual = z.infer<typeof institutionIndividualSchema>;

export type InstitutionIndividualOrDefaults = InstitutionIndividual | Partial<InstitutionIndividual>;

export type DateOfBirthFieldKey = keyof InstitutionIndividual['dateOfBirth'];

export const institutionDetailsIndividualDateOfBirthFields = ['day', 'month', 'year'] satisfies DateOfBirthFieldKey[];

export const reportIndividualNationalClientIdentifierFormatErrors = (individuals: InstitutionIndividualOrDefaults[] = []) =>
  Promise.all(
    individuals.map(({ nationality, nationalClientIdentifier, nationalClientIdentifierType }) =>
      reportNationalClientIdentifierFormatError({ country: nationality, identifier: nationalClientIdentifier, identifierType: nationalClientIdentifierType }),
    ),
  );

type InstitutionDetailsPersonApi = Pick<InstitutionDetailsV2ContactablePerson, 'firstName' | 'lastName' | 'dateOfBirth' | 'countryOfBirth' | 'localityOfBirth' | 'nationality'>;

const institutionDetailsPersonApiToInstitutionIndividual = ({
  firstName,
  lastName,
  dateOfBirth,
  countryOfBirth,
  localityOfBirth,
  nationality,
}: InstitutionDetailsPersonApi): InstitutionIndividualOrDefaults => ({
  firstName,
  lastName,
  dateOfBirth: toDatePartsFromString(dateOfBirth),
  countryOfBirth,
  localityOfBirth,
  nationality,
  nationalClientIdentifierType: undefined,
  nationalClientIdentifier: '',
});

// People from ownership and tax have no individualId, so the same person is matched by name and date of birth
export const getInstitutionIndividualKey = (individual?: DeepPartial<InstitutionIndividualOrDefaults>) =>
  [individual?.firstName, individual?.lastName, individual?.dateOfBirth?.day, individual?.dateOfBirth?.month, individual?.dateOfBirth?.year].join(' ');

export const getInstitutionExistingIndividuals = (decisionMaker: InstitutionDetailsDecisionMaker | undefined, answers: InstitutionDetailsV2Partial | undefined) => {
  const { ownership, taxSelfCertification } = answers ?? {};
  const decisionMakers: InstitutionIndividualOrDefaults[] =
    decisionMaker?.decisionMaker === ControlType.INDIVIDUAL
      ? decisionMaker.individuals.map((individual) => ({ ...individual, dateOfBirth: toDatePartsFromString(individual.dateOfBirth) }))
      : [];
  const people = [
    ...(ownership ? [...('ubos' in ownership ? ownership.ubos : []), ...ownership.management] : []),
    ...(taxSelfCertification && 'controllingPersons' in taxSelfCertification ? taxSelfCertification.controllingPersons : []),
  ].map(institutionDetailsPersonApiToInstitutionIndividual);
  const individuals = [...decisionMakers, ...people];

  return individuals.filter((individual, index) => individuals.findIndex((other) => getInstitutionIndividualKey(other) === getInstitutionIndividualKey(individual)) === index);
};
